import React, { useEffect } from 'react'
import type { DockSide } from '../hooks/useAssistiveFloat'
import { useAssistiveFloat } from '../hooks/useAssistiveFloat'

const DOCK_TAB_WIDTH = 38
const DOCK_TAB_HEIGHT = 64
const HIDE_BTN_SIZE = 34

export interface AssistiveFloatControlsProps {
  floatState: ReturnType<typeof useAssistiveFloat>
  floatSize: number
  zIndex?: number
  onTap: () => void
  ariaLabel: string
  dockAriaLabel?: string
  undockAriaLabel?: string
  hideTitle?: string
  dockBackground?: string
  children: React.ReactNode
  dockedChildren?: React.ReactNode
  hidden?: boolean
}

function nearestDockSide(position: { x: number }, floatSize: number): DockSide {
  if (typeof window === 'undefined') return 'right'
  return position.x + floatSize / 2 >= window.innerWidth / 2 ? 'right' : 'left'
}

export const AssistiveFloatControls: React.FC<AssistiveFloatControlsProps> = ({
  floatState,
  floatSize,
  zIndex = 9998,
  onTap,
  ariaLabel,
  dockAriaLabel = 'ซ่อนปุ่มไว้ขอบจอ',
  undockAriaLabel = 'แสดงปุ่ม',
  hideTitle = 'ซ่อนปุ่มไว้ขอบจอ',
  dockBackground = 'linear-gradient(180deg, #1f6bff 0%, #13c6ff 100%)',
  children,
  dockedChildren,
  hidden = false,
}) => {
  const {
    position,
    isDocked,
    dockSide,
    dock,
    undock,
    bindTap,
    dragHandlers,
  } = floatState

  useEffect(() => {
    bindTap(onTap)
  }, [bindTap, onTap])

  if (hidden) return null

  if (isDocked) {
    const dockStyle: React.CSSProperties = {
      position: 'fixed',
      top: `${position.y}px`,
      width: `${DOCK_TAB_WIDTH}px`,
      height: `${DOCK_TAB_HEIGHT}px`,
      zIndex,
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      padding: '8px 6px',
      color: '#fff',
      background: dockBackground,
      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.28)',
      ...(dockSide === 'right'
        ? { right: 0, borderRadius: '18px 0 0 18px' }
        : { left: 0, borderRadius: '0 18px 18px 0' }),
    }

    return (
      <button type="button" style={dockStyle} onClick={undock} aria-label={undockAriaLabel}>
        {dockedChildren || (
          <>
            <span style={{ fontSize: 16, lineHeight: 1 }}>◉</span>
            <span style={{ fontSize: 14, lineHeight: 1 }}>{dockSide === 'right' ? '‹' : '›'}</span>
          </>
        )}
      </button>
    )
  }

  const willDockTo = nearestDockSide(position, floatSize)
  const hideBtnLeft = willDockTo === 'right'
    ? position.x - HIDE_BTN_SIZE - 8
    : position.x + floatSize + 8
  const hideBtnTop = position.y + (floatSize - HIDE_BTN_SIZE) / 2

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          dock()
        }}
        aria-label={dockAriaLabel}
        title={hideTitle}
        style={{
          position: 'fixed',
          left: `${hideBtnLeft}px`,
          top: `${hideBtnTop}px`,
          width: `${HIDE_BTN_SIZE}px`,
          height: `${HIDE_BTN_SIZE}px`,
          borderRadius: 999,
          background: 'rgba(9, 29, 73, 0.92)',
          color: '#cfe8ff',
          border: '1px solid rgba(96, 165, 250, 0.35)',
          cursor: 'pointer',
          boxShadow: '0 4px 10px rgba(15, 23, 42, 0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          zIndex: zIndex + 1,
          touchAction: 'manipulation',
        }}
      >
        {willDockTo === 'right' ? '›' : '‹'}
      </button>
      <div
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${floatSize}px`,
          height: `${floatSize}px`,
          zIndex,
          touchAction: 'none',
        }}
      >
        <div
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          {...dragHandlers}
          style={{ width: '100%', height: '100%', cursor: 'grab' }}
        >
          {children}
        </div>
      </div>
    </>
  )
}

export interface AssistiveFloatChromeProps {
  storageKey: string
  floatSize: number
  bottomOffset?: number
  zIndex?: number
  getDefaultPosition?: (floatSize: number, bottomOffset: number) => { x: number; y: number }
  onTap: () => void
  ariaLabel: string
  dockAriaLabel?: string
  undockAriaLabel?: string
  hideTitle?: string
  dockBackground?: string
  children: React.ReactNode
  dockedChildren?: React.ReactNode
}

const AssistiveFloatChrome: React.FC<AssistiveFloatChromeProps> = ({
  storageKey,
  floatSize,
  bottomOffset = 24,
  ...rest
}) => {
  const floatState = useAssistiveFloat({ storageKey, floatSize, bottomOffset, getDefaultPosition: rest.getDefaultPosition })

  return (
    <AssistiveFloatControls
      floatState={floatState}
      floatSize={floatSize}
      {...rest}
    />
  )
}

export default AssistiveFloatChrome
